import type { MarketingConsent } from "./consent";
import { assertAllowedPublicRequest, PUBLIC_REQUEST_POLICY } from "../network/publicRequestPolicy";

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const SAVE_KEY = "pp_marketing_operational_queue_v1";
const RETRY_TTL_MS = 7 * 24 * 60 * 60 * 1_000;
type Activation = { consentState: MarketingConsent; transitionId?: string };
// `NonNullable<Parameters<typeof fetch>[1]>` is the identical ambient
// `RequestInit` type; referencing that name directly trips `no-undef`, which
// cannot see TypeScript's ambient lib.dom.d.ts globals.
type FetchInit = NonNullable<Parameters<typeof fetch>[1]>;
type Request = (url: string, init: FetchInit) => Promise<Pick<Response, "ok" | "status">>;
type Payload = {
  event: "cta_activation"; clientEventId: string; occurredAt: string; host: "apex";
  artifactSha: string; consentState: MarketingConsent; transitionId?: string;
};
type Record = { queueId: string; createdAt: number; payload: Payload };

function readQueue(artifactSha: string): Record[] {
  try {
    const value = JSON.parse(localStorage.getItem(SAVE_KEY) ?? "[]");
    if (!Array.isArray(value)) return [];
    return value.filter((record): record is Record => {
      if (!record || typeof record !== "object" || !UUID_V4.test(record.queueId)
        || typeof record.createdAt !== "number" || !record.payload || typeof record.payload !== "object") return false;
      const payload = record.payload as Payload;
      const allowed = new Set(["event", "clientEventId", "occurredAt", "host", "artifactSha", "consentState", "transitionId"]);
      return Object.keys(payload).every((key) => allowed.has(key)) && payload.event === "cta_activation"
        && payload.host === "apex" && payload.artifactSha === artifactSha && UUID_V4.test(payload.clientEventId)
        && ["granted", "denied", "unknown"].includes(payload.consentState)
        && (payload.consentState === "granted" ? UUID_V4.test(payload.transitionId ?? "") : payload.transitionId === undefined);
    });
  } catch { return []; }
}

function writeQueue(queue: Record[]) {
  localStorage.setItem(SAVE_KEY, JSON.stringify(queue.slice(-100)));
}

export function createMarketingActivationAudit(options: {
  artifactSha: string;
  apiOrigin: string;
  request?: Request;
  uuid?: () => string;
}) {
  if (!/^[0-9a-f]{64}$/.test(options.artifactSha)) throw new Error("A 64-character artifact SHA is required");
  let origin: URL;
  try { origin = new URL(options.apiOrigin); } catch { throw new Error("CTA audit API origin is invalid"); }
  const loopback = ["localhost", "127.0.0.1", "[::1]"].includes(origin.hostname.toLowerCase());
  if (origin.href !== `${origin.origin}/` || (origin.protocol !== "https:" && !(origin.protocol === "http:" && loopback))) {
    throw new Error("CTA audit API origin is invalid");
  }
  const endpoint = `${origin.origin}/api/analytics/operational`;
  const request = options.request ?? fetch;
  const uuid = options.uuid ?? (() => crypto.randomUUID());

  const headers = { "Content-Type": "application/json" };
  assertAllowedPublicRequest({ url: endpoint, method: "POST", credentials: "omit", resourceType: "fetch", headers });

  async function flush(now = Date.now()) {
    let queue = readQueue(options.artifactSha).filter((record) => now >= record.createdAt && now - record.createdAt <= RETRY_TTL_MS);
    writeQueue(queue);
    for (const record of [...queue]) {
      try {
        const response = await request(endpoint, {
          method: "POST", credentials: "omit", keepalive: true, headers, body: JSON.stringify(record.payload),
        });
        if (response.ok || (response.status >= 400 && response.status < 500 && response.status !== 429)) {
          queue = queue.filter((item) => item.queueId !== record.queueId);
          writeQueue(queue);
          continue;
        }
      } catch { /* durable retry on the next activation or application mount */ }
      return false;
    }
    return true;
  }

  const record = async (activation: Activation, now = Date.now()) => {
    const clientEventId = uuid();
    if (!UUID_V4.test(clientEventId)) throw new Error("CTA audit clientEventId must be UUIDv4");
    if (activation.consentState === "granted" && !UUID_V4.test(activation.transitionId ?? "")) {
      throw new Error("Granted CTA audit requires a UUIDv4 transitionId");
    }
    if (activation.consentState !== "granted" && activation.transitionId !== undefined) {
      throw new Error("Non-consented CTA audit cannot carry transitionId");
    }
    const payload: Payload = {
      event: "cta_activation", clientEventId, occurredAt: new Date(now).toISOString(),
      host: "apex", artifactSha: options.artifactSha, consentState: activation.consentState,
      ...(activation.transitionId ? { transitionId: activation.transitionId } : {}),
    };
    const queue = readQueue(options.artifactSha);
    queue.push({ queueId: crypto.randomUUID(), createdAt: now, payload });
    writeQueue(queue);
    return flush(now);
  };
  return Object.assign(record, { flush, pending: () => readQueue(options.artifactSha) });
}

const artifactSha = import.meta.env.VITE_ARTIFACT_SHA ?? "";
const singleton = /^[0-9a-f]{64}$/.test(artifactSha)
  ? createMarketingActivationAudit({ artifactSha, apiOrigin: PUBLIC_REQUEST_POLICY.operationalApiOrigin })
  : null;

export function recordMarketingCtaActivation(activation: Activation) {
  void singleton?.(activation);
}

export function flushMarketingCtaActivations() {
  void singleton?.flush();
}
